import { useEffect, useState } from "react";
import { Check, GitPullRequestArrow, Lightbulb, Send, X } from "lucide-react";
import { usePermission } from "@ncp/api-client";
import { formatDateTime, formatRelative, useLocale, useTranslation } from "@ncp/i18n";
import { FileChanges, PageHost, type PageSuggestion } from "@ncp/page-host";
import { Badge, Button, EmptyState, Field, InlineAlert, MarkdownBody, Skeleton, Tabs, TabsList, TabsTrigger, Textarea, ToggleGroup, ToggleGroupItem, cn, notifySaved } from "@ncp/ui";
import { usePageActions, usePageSuggestions, useSuggestionDiff } from "../api";

export const suggestionTone = (s: string) => (s === "Submitted" ? "warning" : s === "Accepted" ? "success" : s === "Declined" || s === "Failed" ? "danger" : "outline") as "warning" | "success" | "danger" | "outline";

/** Changes people asked for with "Suggest a change", and personal pages offered to a space, waiting for an author's decision. */
export function SuggestionsTab({ pageKey, onAccepted }: { pageKey: string; onAccepted: () => void }) {
  const { t } = useTranslation();
  const list = usePageSuggestions(pageKey);
  const [filter, setFilter] = useState<"open" | "all" | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const waiting = (list.data ?? []).some((s) => s.status === "Submitted");
  const shown = filter ?? (waiting ? "open" : "all");
  const rows = (list.data ?? []).filter((s) => shown === "all" || s.status === "Submitted");
  const current = rows.find((s) => s.id === selected) ?? rows[0] ?? null;

  if (list.isLoading) return <Skeleton className="h-64" />;
  if ((list.data ?? []).length === 0) return <EmptyState icon={Lightbulb} title={t("admin:pageStudio.suggestions.emptyTitle")} description={t("admin:pageStudio.suggestions.emptyBody")} />;

  return (
    <div className="grid gap-4 lg:grid-cols-[320px_minmax(0,1fr)]">
      <aside className="space-y-2">
        <ToggleGroup type="single" size="sm" value={shown} onValueChange={(v) => v && setFilter(v as "open" | "all")}>
          <ToggleGroupItem value="open">{t("admin:pageStudio.suggestions.open")}</ToggleGroupItem>
          <ToggleGroupItem value="all">{t("admin:common.all")}</ToggleGroupItem>
        </ToggleGroup>
        <SuggestionList rows={rows} selected={current?.id ?? null} onSelect={setSelected} />
      </aside>
      {current ? <SuggestionReview key={current.id} suggestion={current} onDecided={(s) => { void list.refetch(); if (s.status === "Accepted") onAccepted(); }} /> : null}
    </div>
  );
}

export function SuggestionList({ rows, selected, onSelect, showPage = false }: { rows: PageSuggestion[]; selected: string | null; onSelect: (id: string) => void; showPage?: boolean }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  if (rows.length === 0) return <p className="px-1 text-sm text-muted-foreground">{t("admin:pageStudio.suggestions.noneWaiting")}</p>;
  return (
    <ul className="space-y-1.5">
      {rows.map((s) => (
        <li key={s.id}>
          <button type="button" onClick={() => onSelect(s.id)} className={cn("w-full rounded-xl border px-3 py-2 text-start transition-colors", s.id === selected ? "border-aegold-300 bg-aegold-50/50" : "border-sand-200 bg-white hover:bg-sand-50")}>
            <span className="flex items-center gap-1.5">
              {s.kind === "promote" ? <GitPullRequestArrow className="size-3.5 text-seablue-700" /> : <Lightbulb className="size-3.5 text-aegold-700" />}
              <span className="min-w-0 flex-1 truncate text-sm font-medium">{showPage ? (s.pageName ?? s.pageKey) : s.request}</span>
              <Badge size="sm" variant={suggestionTone(s.status)}>{t(`admin:pageStudio.suggestions.status.${s.status}`, { defaultValue: s.status })}</Badge>
            </span>
            {showPage ? <span className="mt-0.5 block truncate text-xs">{s.request}</span> : null}
            <span className="mt-0.5 block text-[11px] text-muted-foreground">{s.requestedByName} · {formatRelative(s.submittedAt ?? s.createdAt, locale)} · {t("admin:pageStudio.suggestions.files", { count: s.changedFiles.length })}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}

/** One suggestion: what was asked, what the AI author changed, the private copy running, and the decision. */
export function SuggestionReview({ suggestion: s, onDecided }: { suggestion: PageSuggestion; onDecided: (s: PageSuggestion) => void }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { can } = usePermission();
  const diff = useSuggestionDiff(s.id);
  const actions = usePageActions();
  const [view, setView] = useState<"changes" | "preview">("changes");
  const [note, setNote] = useState("");
  useEffect(() => { setNote(""); }, [s.id]);
  const open = s.status === "Submitted";
  const canDecide = open && can("pages.author");

  const decide = async (accept: boolean) => {
    try {
      const r = accept ? await actions.accept.mutateAsync({ id: s.id, note }) : await actions.decline.mutateAsync({ id: s.id, note });
      notifySaved(r, accept ? t("admin:pageStudio.suggestions.accepted") : t("admin:pageStudio.suggestions.declined"));
      onDecided(r);
    } catch { /* useSave reported it */ }
  };

  return (
    <article className="min-w-0 space-y-3 rounded-xl border border-sand-200 bg-white p-4">
      <header className="flex flex-wrap items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <Badge size="sm" variant={s.kind === "promote" ? "info" : "gold"}>{t(`admin:pageStudio.suggestions.kind.${s.kind}`, { defaultValue: s.kind })}</Badge>
            <Badge size="sm" variant={suggestionTone(s.status)}>{t(`admin:pageStudio.suggestions.status.${s.status}`, { defaultValue: s.status })}</Badge>
            <span className="text-xs text-muted-foreground">{t("admin:pageStudio.suggestions.from", { name: s.requestedByName ?? "—", when: formatDateTime(s.submittedAt ?? s.createdAt, locale) })}</span>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            {s.kind === "promote" ? t("admin:pageStudio.suggestions.promoteInto", { space: s.spaceKey ?? "—", key: s.targetKey ?? s.pageKey }) : t("admin:pageStudio.suggestions.basedOn", { version: s.baseVersion })}
          </p>
        </div>
      </header>
      <blockquote className="rounded-lg border-s-4 border-aegold-300 bg-sand-50 px-3 py-2 text-sm">{s.request}</blockquote>
      {s.summary ? <div className="text-sm"><p className="mb-1 text-xs font-semibold text-sand-700">{t("admin:pageStudio.suggestions.summary")}</p><MarkdownBody>{s.summary}</MarkdownBody></div> : null}

      <Tabs value={view} onValueChange={(v) => setView(v as "changes" | "preview")}>
        <TabsList>
          <TabsTrigger value="changes">{t("admin:pageStudio.suggestions.changes", { count: s.changedFiles.length })}</TabsTrigger>
          <TabsTrigger value="preview" disabled={!s.previewDefinitionId}>{t("admin:pageStudio.suggestions.preview")}</TabsTrigger>
        </TabsList>
      </Tabs>
      {view === "changes" ? (diff.isLoading ? <Skeleton className="h-40" /> : diff.isError ? <InlineAlert tone="danger">{(diff.error as Error).message}</InlineAlert> : <FileChanges changes={diff.data ?? []} />) : null}
      {view === "preview" && s.previewDefinitionId ? (
        <div className="h-[60vh] overflow-hidden rounded-xl border border-sand-200">
          <PageHost pageKey={s.previewKey} definitionId={s.previewDefinitionId} space={s.spaceKey} />
        </div>
      ) : null}

      {canDecide ? (
        <div className="space-y-2 border-t border-sand-100 pt-3">
          <Field label={t("admin:pageStudio.suggestions.note")} hint={t("admin:pageStudio.suggestions.noteHint")}><Textarea value={note} onChange={(e) => setNote(e.target.value)} className="min-h-[60px]" /></Field>
          <div className="flex flex-wrap gap-2">
            <Button variant="gold" loading={actions.accept.isPending} onClick={() => void decide(true)}><Check /> {s.kind === "promote" ? t("admin:pageStudio.suggestions.acceptPromote") : t("admin:pageStudio.suggestions.accept")}</Button>
            <Button variant="outline" loading={actions.decline.isPending} onClick={() => void decide(false)}><X /> {t("admin:pageStudio.suggestions.decline")}</Button>
          </div>
          <p className="text-[11px] text-muted-foreground">{s.kind === "promote" ? t("admin:pageStudio.suggestions.acceptPromoteHint") : t("admin:pageStudio.suggestions.acceptHint")}</p>
        </div>
      ) : s.decidedAt ? (
        <p className="border-t border-sand-100 pt-3 text-xs text-muted-foreground"><Send className="me-1 inline size-3" />{t("admin:pageStudio.suggestions.decided", { name: s.decidedByName ?? "—", when: formatDateTime(s.decidedAt, locale) })}{s.decisionNote ? ` · ${s.decisionNote}` : ""}</p>
      ) : null}
    </article>
  );
}
